import { ref, type Ref, onMounted } from 'vue';
import { useApplicationStore } from "@/stores/Application";
import { useNuxtApp } from '#app';
import { DebugSettings } from '@ca.vmedia.tv/domains';

export interface IUseDevModeState {
    isEnabledDevMode: Ref<boolean>;
    dataLoaded: Ref<boolean>;
    activateDevMode: () => void;
    deactivateDevMode: () => void;
}

export const useDevModeState = (): IUseDevModeState => {
    const ApplicationStore = useApplicationStore();
    const {
        openModalReloadApp,
        setIsEnabledDevMode,
    } = ApplicationStore;
    const {
        getIsEnabledDevMode: isEnabledDevMode,
    } = storeToRefs(ApplicationStore);

    const app = useNuxtApp();
    const { featureDevModeAvailable: devModeAvailable } = useAppConfig();
    
    const dataLoaded: Ref<boolean> = ref(false);
    const clickCount: Ref<number> = ref(0);
    const clickTimeout: Ref<ReturnType<typeof setTimeout> | null> = ref(null);
    
    const resetClickHandler = (): void => {
        clickCount.value = 0;
        if (clickTimeout.value) {
            clearTimeout(clickTimeout.value);
        }
    }
    
    const activateDevMode = (): void => {
        if (isEnabledDevMode.value || !devModeAvailable) {
            return;
        }
        if (clickTimeout.value) {
            clearTimeout(clickTimeout.value);
        }
        clickTimeout.value = setTimeout(resetClickHandler, 10000);
        clickCount.value++;
        
        if (clickCount.value >= 5) {
            resetClickHandler();
            app.$domains.lsCacher.put('debug-settings', new DebugSettings());
            setIsEnabledDevMode(true);
        }
    }
    
    const deactivateDevMode = (): void => {
        app.$domains.lsCacher.del('debug-settings');
        openModalReloadApp();
        setIsEnabledDevMode(false);
    }

    onMounted(() => {
        if (devModeAvailable) { 
            const isEnabled = app.$domains.lsCacher.has('debug-settings');
            setIsEnabledDevMode(isEnabled);
            dataLoaded.value = true
        }
    })

    return {
        isEnabledDevMode,
        activateDevMode,
        deactivateDevMode,
        dataLoaded
    }
}
